import * as yup from 'yup';

export interface IMetaWhen {
  when: string;
  then: (args: any[], schema: any) => any;
}

declare module 'yup' {
  interface Schema {
    /**
     * Imposta un when nel meta, che può essere utilizzato successivamente.
     * In questo modo, è possibile configurare un when che verrà però utilizzato successivamente
     * in fase di building dello schema.
     *
     * e.g.
     * ```
     *  schema
     * .when('field', (_, schema) =>
     *   schema.meta()?.variable ? schema.required() : schema.notRequired(),
     * )
     * .when('field2', (_, schema) => schema.meta({ variable: true }));
     * ```
     * In quessto esempio, il primo when non vedrà "variable" perché viene impostato successivamente.
     * Con meta when possiamo fare:
     * ```
     * schema
     * .metaWhen("example", { when: 'field}, then:  (_, schema) => schema.meta()?.variable ? schema.required() : schema.notRequired())
     * .when('field2', (_, schema) => schema.meta({ variable: true }));
     *
     * // Successivamente, altrove nel codice
     * schema = setMetaWhen("example", schema);
     * ```
     * In questo modo schema.meta()?.variable sarà valorizzato.
     */
    metaWhen(tag: string, input: IMetaWhen | IMetaWhen[]): this;
  }
}

function registerForSchema<T extends yup.Schema>(schema: (...arg: any[]) => T) {
  yup.addMethod<T>(
    schema,
    'metaWhen',
    function (this: T, tag: string, input: IMetaWhen | IMetaWhen[]) {
      const data = Array.isArray(input) ? input : [input];

      return this.meta({
        metaWhen: {
          ...this.meta()?.metaWhen,
          [tag]: data,
        },
      });
    },
  );
}

registerForSchema(yup.object);
registerForSchema(yup.array);
registerForSchema(yup.string);
registerForSchema(yup.number);
registerForSchema(yup.boolean);
registerForSchema(yup.date);
registerForSchema(yup.mixed);

/**
 * Funzione che applica i metaWhen allo schema.
 */
export function setMetaWhen(tag: string, schema: yup.AnySchema) {
  const metaWhen: IMetaWhen[] | undefined = schema.meta()?.metaWhen?.[tag];
  if (!metaWhen || metaWhen.length === 0) return schema;
  return metaWhen.reduce((acc, { when, then }) => acc.when(when, then), schema);
}
